import { useState } from 'react';
import { Link } from 'react-router-dom';
import { Blocks, Plug, AlertTriangle } from 'lucide-react';
import Card from '@/components/ui/Card';
import Button from '@/components/ui/Button';
import LoadingSpinner from '@/components/ui/LoadingSpinner';
import {
  CATEGORY_LABELS,
  CATEGORY_ORDER,
  useIntegrationProviders,
  type ProviderSummary,
} from '@/hooks/useIntegrations';
import StatusBadge from './integrations/StatusBadge';
import ConnectModal from './integrations/ConnectModal';
import LegacyIntegrations from './integrations/LegacyIntegrations';

/**
 * Admin → Integrations
 *
 * Provider grid driven by GET /api/admin/integrations/providers (one card per
 * registered connector, grouped by category), plus the legacy platform
 * services (AXISS, ETaxEngine, Ticketmaster) underneath.
 */
export default function Integrations() {
  const { data, isLoading, error } = useIntegrationProviders();
  const [connecting, setConnecting] = useState<ProviderSummary | null>(null);

  const providers = data?.providers ?? [];
  const grouped = CATEGORY_ORDER
    .map((cat) => ({ cat, items: providers.filter((p) => p.category === cat) }))
    .filter((g) => g.items.length > 0);

  // 403 = the `integrations` feature flag is off for this tenant.
  const disabled = (error as any)?.status === 403 || /disabled/i.test((error as any)?.message || '');

  return (
    <div className="space-y-8">
      <div className="flex items-center gap-3">
        <Blocks className="h-6 w-6 text-ink-soft" aria-hidden="true" />
        <div>
          <h1 className="text-2xl font-semibold text-ink">Integrations</h1>
          <p className="text-sm text-ink-muted">
            Connect the services your store already runs on. Orders, products and customers sync automatically.
          </p>
        </div>
      </div>

      {isLoading ? (
        <div className="flex h-40 items-center justify-center">
          <LoadingSpinner />
        </div>
      ) : disabled ? (
        <Card>
          <div className="flex gap-3 text-sm text-ink-soft">
            <AlertTriangle className="mt-0.5 h-5 w-5 shrink-0 text-amber-600" aria-hidden="true" />
            <p>
              Third-party integrations are turned off for this site. Enable the <code>integrations</code> flag under{' '}
              <Link to="/admin/feature-toggles" className="underline">Settings → Feature Toggles</Link> to connect services.
            </p>
          </div>
        </Card>
      ) : error ? (
        <Card>
          <p className="text-sm text-red-600" role="alert">{(error as any)?.message || 'Failed to load integrations'}</p>
        </Card>
      ) : providers.length === 0 ? (
        <Card>
          <div className="flex items-center gap-3 text-sm text-ink-soft">
            <Plug className="h-5 w-5 shrink-0" aria-hidden="true" />
            <p>No connectors are available on this release yet. ShipStation, Klaviyo, Mailchimp and Yotpo are rolling out next.</p>
          </div>
        </Card>
      ) : (
        grouped.map(({ cat, items }) => (
          <section key={cat} className="space-y-3" aria-labelledby={`cat-${cat}`}>
            <h2 id={`cat-${cat}`} className="text-sm font-semibold uppercase tracking-wide text-ink-muted">
              {CATEGORY_LABELS[cat]}
            </h2>
            <div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-3">
              {items.map((p) => (
                <ProviderCard key={p.id} provider={p} onConnect={() => setConnecting(p)} />
              ))}
            </div>
          </section>
        ))
      )}

      <LegacyIntegrations />

      {connecting && (
        <ConnectModal
          provider={connecting}
          isOpen={!!connecting}
          onClose={() => setConnecting(null)}
        />
      )}
    </div>
  );
}

function ProviderCard({ provider, onConnect }: { provider: ProviderSummary; onConnect: () => void }) {
  const connected = provider.status === 'connected' || provider.status === 'error';
  const { ok, error } = provider.stats_24h;
  return (
    <Card className="h-full">
     <div className="flex h-full flex-col gap-4" data-testid={`provider-${provider.id}`}>
      <div className="flex items-start justify-between gap-3">
        <div>
          <h3 className="text-base font-semibold text-ink">{provider.name}</h3>
          <p className="mt-1 text-sm text-ink-soft">{provider.description}</p>
        </div>
        <StatusBadge status={provider.status} />
      </div>

      {provider.status === 'error' && provider.last_error && (
        <p className="truncate text-xs text-red-600" title={provider.last_error}>{provider.last_error}</p>
      )}

      {connected && (ok > 0 || error > 0) && (
        <p className="text-xs text-ink-muted">
          Last 24h: {ok} ok{error > 0 ? `, ${error} failed` : ''}
        </p>
      )}

      <div className="mt-auto flex items-center justify-between gap-2">
        <span>
          {provider.status === 'disconnected' && (
            <Button size="sm" onClick={onConnect}>Connect</Button>
          )}
        </span>
        <Link to={`/admin/integrations/${provider.id}`} className="text-sm text-sage-700 underline">
          {connected ? 'Manage' : 'Details'}
        </Link>
      </div>
     </div>
    </Card>
  );
}
